import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { createElement } from 'react';
import styled from 'styled-components';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["area", "style"];
var defaultGap = ["0px", "0px"];
var StyledGrid = styled.div.withConfig({
  displayName: "Layout__StyledGrid",
  componentId: "sc-18y4etc-0"
})(["&&{display:grid;grid-template-columns:", ";grid-template-rows:", ";display:-ms-grid;-ms-grid-columns:", ";-ms-grid-rows:", ";}"], function (props) {
  var _props$columns;

  return props === null || props === void 0 ? void 0 : (_props$columns = props.columns) === null || _props$columns === void 0 ? void 0 : _props$columns.join(" ");
}, function (props) {
  var _props$rows;

  return props === null || props === void 0 ? void 0 : (_props$rows = props.rows) === null || _props$rows === void 0 ? void 0 : _props$rows.join(" ");
}, function (props) {
  var _props$columns2;

  return props === null || props === void 0 ? void 0 : (_props$columns2 = props.columns) === null || _props$columns2 === void 0 ? void 0 : _props$columns2.join(" ");
}, function (props) {
  var _props$rows2;

  return props === null || props === void 0 ? void 0 : (_props$rows2 = props.rows) === null || _props$rows2 === void 0 ? void 0 : _props$rows2.join(" ");
});
var StyledItem = styled.div.withConfig({
  displayName: "Layout__StyledItem",
  componentId: "sc-18y4etc-1"
})(["&&{align-self:", ";display:block;grid-column:", ";grid-row:", ";justify-self:", ";-ms-grid-column:", ";-ms-grid-column-align:", ";-ms-grid-column-span:", ";-ms-grid-row:", ";-ms-grid-row-align:", ";-ms-grid-row-span:", ";}"], function (props) {
  return props.align;
}, function (props) {
  return props === null || props === void 0 ? void 0 : props.column.join(" / ");
}, function (props) {
  return props === null || props === void 0 ? void 0 : props.row.join(" / ");
}, function (props) {
  return props.justify;
}, function (props) {
  return props === null || props === void 0 ? void 0 : props.column[0];
}, function (props) {
  return props.justify;
}, function (props) {
  return props === null || props === void 0 ? void 0 : props.column[1];
}, function (props) {
  return props === null || props === void 0 ? void 0 : props.row[0];
}, function (props) {
  return props.align;
}, function (props) {
  return props === null || props === void 0 ? void 0 : props.row[1];
});

function get2DMax(arr) {
  return arr.sort(function (a, b) {
    return b.length - a.length;
  })[0];
}

function padArray(arr, length, value) {
  return arr.concat(Array.from({
    length: length - arr.length
  }, function () {
    return value;
  }));
}

function insertBetween(arr, value) {
  return arr.reduce(function (v, e, i) {
    if (i === arr.length - 1) return v.concat([e]);
    return v.concat([e, value]);
  }, []);
}

function verifyValues(values) {
  values.forEach(function (v) {
    if (v.match(/auto-(fill|fit)/)) {
      throw new Error("The values `auto-fit` and `auto-fill` are not supported.");
    }

    if (v.match(/repeat\(([0-9]+),\s?(([0-9]|[a-z]|\[|\]|-|\s|px|cm|vw|ch|%|\(|\)|,)+)\)$/)) {
      throw new Error("The value `repeat` is not supported. Please use native JS functions if you need to repeat values.");
    }

    if (v === "auto" && !values.join(" ").includes("fr") || v.includes("minmax") && v.includes("auto")) {
      throw new Error("The value `auto` is not supported when used without `fr` or when used inside `minmax`.");
    }
  });
}

function getTracks(type, areas, template, gap) {
  var areas2D = areas.map(function (a) {
    return a.split(" ");
  });
  var rowLength = get2DMax(areas2D).length;
  var padLength = type === "columns" ? rowLength : areas.length;
  var templatePadded = padArray(template, padLength, "auto");
  var templateWithGaps = insertBetween(templatePadded, gap);

  var getCells = function getCells(index) {
    if (type === "columns") return areas2D.map(function (row) {
      return row[index];
    });
    return areas2D[index];
  };

  return templateWithGaps.map(function (s, i) {
    var isGap = i % 2;
    var initIndex = i === 0 ? i : i - i / 2;
    var cellStyles = {};
    var size = s;
    var isFit = size.match(/fit-content\((([0-9])+(px|cm|vw|ch|%))\)/);

    if (isFit) {
      var prop = type === "columns" ? "maxWidth" : "maxHeight";
      cellStyles[prop] = isFit[1];
      size = "auto";
    }

    return {
      startLine: i + 1,
      endLine: i + 2,
      cells: isGap ? [] : getCells(initIndex),
      cellStyles: cellStyles,
      size: size
    };
  });
}

function getAreaArray(area) {
  var arr = Array.isArray(area) ? area : [area];
  if (arr.length === 4) return arr;
  if (arr.length === 2) return arr;
  if (arr.length === 3) arr.push(arr[1]);
  if (arr.length === 1) arr.push(arr[0]);
  return arr;
}

function getItemProps(area, columns, rows) {
  var areaArr = getAreaArray(area);
  var props = {
    column: [],
    row: [],
    style: {}
  };
  areaArr.forEach(function (a, i) {
    var column = columns.find(function (line) {
      return line.cells.includes(a);
    });
    var row = rows.find(function (line) {
      return line.cells.includes(a);
    });
    var isStart = i <= 1;
    var isColumn = i % 2;

    if (isColumn && column) {
      props.column.push(isStart ? column.startLine : column.endLine);
      props.style = _objectSpread(_objectSpread({}, props.style), column.cellStyles);
    } else if (row) {
      props.row.push(isStart ? row.startLine : row.endLine);
      props.style = _objectSpread(_objectSpread({}, props.style), row.cellStyles);
    } else if (process.env.NODE_ENV !== "production") {
      console.warn("Layout: couldn't find column or row for area \"".concat(a, "\"."));
    }
  });
  return props;
}

var Layout = /*#__PURE__*/React.forwardRef(function Layout2(props, ref) {
  var areas = props.areas,
      items = props.items,
      columns = props.columns,
      rows = props.rows,
      _props$gap = props.gap,
      gap = _props$gap === void 0 ? defaultGap : _props$gap,
      style = props.style;
  var columnTracks = getTracks("columns", areas, columns, gap[0]);
  var rowTracks = getTracks("rows", areas, rows, gap[1] || defaultGap[1]);
  var columnsTemplate = columnTracks.map(function (c) {
    return c.size;
  });
  var rowsTemplate = rowTracks.map(function (r) {
    return r.size;
  });

  if (process.env.NODE_ENV !== "production") {
    verifyValues(columnsTemplate);
    verifyValues(rowsTemplate);
  }

  return /*#__PURE__*/jsx(StyledGrid, {
    columns: columnsTemplate,
    rows: rowsTemplate,
    ref: ref,
    style: style,
    children: items.map(function (_ref, i) {
      var area = _ref.area,
          itemStyle = _ref.style,
          rest = _objectWithoutProperties(_ref, _excluded);

      var itemProps = getItemProps(area, columnTracks, rowTracks);
      return /*#__PURE__*/createElement(StyledItem, _objectSpread(_objectSpread(_objectSpread({}, rest), itemProps), {}, {
        style: _objectSpread(_objectSpread({}, itemStyle), itemProps.style),
        key: i
      }));
    })
  });
});
Layout.propTypes = {
  "areas": _PropTypes.arrayOf(_PropTypes.string).isRequired,
  "columns": _PropTypes.arrayOf(_PropTypes.string).isRequired,
  "gap": _PropTypes.arrayOf(_PropTypes.string),
  "items": _PropTypes.arrayOf(_PropTypes.shape({
    "align": _PropTypes.oneOf(["center", "end", "start", "stretch"]),
    "area": _PropTypes.oneOfType([_PropTypes.arrayOf(_PropTypes.string), _PropTypes.string]).isRequired,
    "children": _PropTypes.node,
    "justify": _PropTypes.oneOf(["center", "end", "start", "stretch"]),
    "style": _PropTypes.object
  })).isRequired,
  "rows": _PropTypes.arrayOf(_PropTypes.string).isRequired,
  "style": _PropTypes.object
};
Layout.displayName = "Layout";

export { Layout };
